/**
 * Field-state vocabulary — the four agreed value states (Derived-Value
 * Conflict Rule v1.0, 22 Sep) plus the ways a figure can be absent.
 *
 * ONE shared model and ONE token set for how any deal field renders. Every
 * present figure carries exactly one of four states:
 *
 *   stated          a document said it — the file, the page or cell, the date
 *   derived         the platform computed it — the formula and the inputs
 *   suggested       an assumption or a default — never counted as data until
 *                   a person confirms it
 *   confirmed       a person set or accepted it — who, when and why
 *
 * and a field with no figure is one of:
 *
 *   conflicted      two sources state different INPUTS; a person picks one
 *                   (rule R1 — people resolve inputs, never outputs)
 *   missing         expected for this stage, absent
 *   not_applicable  structurally unavailable — a human decision or wrong deal type
 *   in_unread_model an attached model OFFERS this datapoint; it is not yet read
 *   in_unread_attachment  blank, and an attached file the engine could not read
 *                   (a scan with no text layer) may state it
 *
 * "Extracted · source not recorded" is retired. A value whose source the
 * platform cannot name is a SUGGESTION, not an unsourced extraction — the old
 * fallback made an engine-computed value, a platform default and a person's
 * typing all read as a document statement with no document.
 *
 * Chris named "derived" as the thing AI tooling gets wrong, so it carries its
 * own colour (violet) and is never quietly styled like a stated value. A
 * suggestion is drawn with a dashed outline: provisional, not data.
 */

export type FieldState =
  | 'stated'
  | 'derived'
  | 'suggested'
  | 'confirmed'
  | 'conflicted'
  | 'missing'
  | 'not_applicable'
  | 'in_unread_model'
  | 'in_unread_attachment'

/** Any state word the platform stores or returns, mapped onto the display
 *  vocabulary. The lineage API returns the agreed state itself (`state`);
 *  prefer {@link stateOfLineage} for a lineage row, which reads it. This maps
 *  the raw engine words (`valueState`) for surfaces that only have those.
 *  Keeping this the single mapping means no caller invents its own. */
export function toFieldState(valueState: string | null | undefined): FieldState {
  switch (valueState) {
    case 'stated':
    case 'imported':
    case 'extracted':
      return 'stated'
    case 'derived':
    case 'inferred':
      return 'derived'
    // "recorded" meant "no source recorded" — a suggestion, never an
    // extraction.
    case 'suggested':
    case 'recorded':
      return 'suggested'
    case 'confirmed':
    case 'manual':
    case 'approved':
    case 'overridden':
      return 'confirmed'
    case 'conflicted':
    case 'conflicting':
    case 'conflict':
      return 'conflicted'
    case 'not_applicable':
      return 'not_applicable'
    case 'in_unread_model':
      return 'in_unread_model'
    case 'in_unread_attachment':
    case 'unread_attachment':
      return 'in_unread_attachment'
    case 'missing':
    default:
      return 'missing'
  }
}

export const FIELD_STATE_TOKENS: Record<
  FieldState,
  { label: string; className: string; dot: string; description: string }
> = {
  stated: {
    label: 'Stated',
    className: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
    dot: 'bg-sky-400',
    description: 'Read from a document — the file, the page or cell, and the date',
  },
  derived: {
    label: 'Derived',
    className: 'bg-violet-500/10 text-violet-300 ring-violet-500/25',
    dot: 'bg-violet-400',
    description: 'Calculated by the platform — the formula and the inputs',
  },
  suggested: {
    label: 'Suggested',
    className: 'bg-transparent text-stone-300 ring-0 border border-dashed border-stone-400/50',
    dot: 'bg-stone-400',
    description: 'An assumption or a default — not data until a person confirms it',
  },
  confirmed: {
    label: 'Confirmed',
    className: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25',
    dot: 'bg-emerald-400',
    description: 'Set or accepted by a person — who, when and why',
  },
  conflicted: {
    label: 'Conflict',
    className: 'bg-red-500/10 text-red-300 ring-red-500/25',
    dot: 'bg-red-400',
    description: 'Two sources state different inputs — a person chooses one',
  },
  missing: {
    label: 'Missing',
    className: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
    dot: 'bg-amber-400',
    description: 'Expected for this stage, absent',
  },
  not_applicable: {
    label: 'N/A',
    className: 'bg-stone-500/10 text-stone-400 ring-stone-500/20',
    dot: 'bg-stone-500',
    description: 'Structurally unavailable for this deal',
  },
  in_unread_model: {
    label: 'In unread model',
    className: 'bg-indigo-500/10 text-indigo-300 ring-indigo-500/25',
    dot: 'bg-indigo-400',
    description: 'An attached model the engine has not read may state this — not yet in the record',
  },
  in_unread_attachment: {
    label: 'In unread attachment',
    className: 'bg-indigo-500/10 text-indigo-300 ring-indigo-500/25',
    dot: 'bg-indigo-400',
    description: 'An attached file the engine could not read (no text layer) may state this — not yet in the record',
  },
}

/**
 * The one field-state badge. Accepts either a canonical FieldState or a raw
 * state word (mapped for you), so every render site — deal dossier,
 * inspector, intake — shows the same states the same way.
 */
export function FieldStateBadge({
  state,
  formula,
  title,
  className = '',
}: {
  state: FieldState | string
  /** For a derived value: the formula/inputs, surfaced on hover. */
  formula?: string | null
  /** A fuller hover text — the source line, the basis, who confirmed it. */
  title?: string | null
  className?: string
}) {
  const canonical: FieldState = isFieldState(state) ? state : toFieldState(state)
  const token = FIELD_STATE_TOKENS[canonical]
  const hover =
    title ||
    (canonical === 'derived' && formula ? `${token.description}: ${formula}` : token.description)
  return (
    <span
      title={hover}
      data-field-state={canonical}
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide ring-1 ring-inset ${token.className} ${className}`}
    >
      {token.label}
    </span>
  )
}

function isFieldState(value: string): value is FieldState {
  return value in FIELD_STATE_TOKENS
}
